import { Tabs } from "flowbite-react"
import { FormikProps, useFormikContext } from "formik"
import posthog from "posthog-js"
import React, { useEffect, useState } from "react"

import { CashIcon, CreditIcon } from "@assets/icons"
import GiftIcon from "@icons/Gift"
import SquareCreditCard from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/SquareCreditCardForm"
import { SquareCreditCardFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/SquareCreditCardForm/SquareCreditCardForm"
import StripeCreditCard from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/StripeCreditCardForm"
import { StripeCreditCardFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/StripeCreditCardForm/StripeCreditCardForm"
import {
  CreateCartResponseType,
  GetCartResponseType,
  PaymentTypes,
  ValidateGiftcardCodeResponseType,
} from "@mono/essentials/types/CartTypes"
import {
  GetIntegrationResponseType,
  IntegrationResponseType,
} from "@mono/essentials/types/IntegrationTypes"
import { GetOrderDetailsResponseType } from "@mono/essentials/types/OrderTypes"

interface PaymentSectionProps {
  integratedData: GetIntegrationResponseType
  cartData?: GetCartResponseType["data"]
  stripeCreditCardFormRef?: React.RefObject<FormikProps<StripeCreditCardFormAttributes>>
  handlePaymentError?: () => void
  cartId?: string
  isIntegratedPage?: boolean
  onsucess?: (orderData?: GetOrderDetailsResponseType) => void
  giftcardDetails?: ValidateGiftcardCodeResponseType["giftcard"]
  sqaureUpRef?: any
  squareUpFormRef?: React.RefObject<FormikProps<SquareCreditCardFormAttributes>>
  handleCreateOrder?: () => void
  originalDeposit?: number
  showPendingBookingPaymentConfirmation?: (arg0: boolean) => void
  handlePaymentConfirmation?: boolean
}

const PaymentSection = ({
  integratedData = [],
  cartData,
  stripeCreditCardFormRef,
  handlePaymentError,
  cartId,
  isIntegratedPage,
  onsucess,
  giftcardDetails,
  sqaureUpRef,
  squareUpFormRef,
  handleCreateOrder,
  showPendingBookingPaymentConfirmation,
  handlePaymentConfirmation,
}: PaymentSectionProps) => {
  const { values, setFieldValue } = useFormikContext<any>()
  const [filteredIntegrations, setFilteredIntegrations] = useState<IntegrationResponseType[]>([])

  useEffect(() => {
    setFilteredIntegrations(
      integratedData?.filter(
        (integration) =>
          integration.name.toLowerCase() === "stripe" ||
          integration.name.toLowerCase() === "squareup"
      ) ?? []
    )
  }, [integratedData])

  const isDepositAmount =
    Boolean(cartData?.deposit_auto_confirm || cartData?.is_deposit_enabled_for_items) &&
    values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
      ? cartData?.deposit_pay_now?.amount_in_units
      : 0

  return (
    <>
      {filteredIntegrations.length === 1 ? (
        <>
          {filteredIntegrations?.map((integration) => {
            return (
              <div key={integration?.id}>
                {integration.name.toLocaleLowerCase() === "stripe" &&
                  integration.status === "active" && (
                    <StripeCreditCard
                      paymentAmountDetails={{
                        totalAmount: cartData?.total,
                        totalAmountFormatted: cartData?.formatted_total,
                        depositAmount: isDepositAmount,
                        depositAmountFormatted: cartData?.formatted_deposit,
                        totalTip: cartData?.tips.length ? cartData?.tips[0].amount : undefined,
                        totalTipFormatted: cartData?.tips.length
                          ? cartData?.tips[0].formatted_amount
                          : undefined,
                        totalAmountWithTip:
                          cartData?.full_amount_pay_now?.amount_in_units ??
                          cartData?.total_with_tip,
                        totalAmountWithTipInCents:
                          cartData?.full_amount_pay_now?.amount ??
                          cartData?.total_with_tip_in_cents,
                        totalAmountWithTipFormatted: cartData?.formatted_total_with_tip,
                        depostAmountWithTip: cartData?.deposit_pay_now?.amount_in_units,
                        depostAmountWithTipFormatted: cartData?.formatted_deposit,
                        depositAmountWithTipInCents: cartData?.deposit_pay_now?.amount,
                        isDepositEnabledForItems:
                          Boolean(isDepositAmount) ||
                          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT,
                      }}
                      stripeKey={integration.config.client_id}
                      stripeAccountId={integration.config.stripe_account_id}
                      handleClose={() => {}}
                      stripeCreditCardFormRef={stripeCreditCardFormRef}
                      hideDefaultContainer
                      onError={handlePaymentError}
                      cartId={cartId}
                      stripePaymentId={integration.id}
                      isIntegratedPage={isIntegratedPage}
                      onOrderCreation={(orderData) => onsucess?.(orderData)}
                      giftcardDetails={giftcardDetails}
                      isWidgetsCard={!cartData?.customer?.is_member}
                      customer={cartData?.customer}
                      hideSavedCards
                      isAuthUser={Boolean(cartData?.customer?.is_member)}
                      showPendingBookingPaymentConfirmation={showPendingBookingPaymentConfirmation}
                      handlePaymentConfirmation={handlePaymentConfirmation}
                      isWalletsEnabled={
                        posthog.isFeatureEnabled("enable_wallets") && Number(values?.amount) > 0
                      }
                    />
                  )}

                {integration.name.toLocaleLowerCase() === "squareup" &&
                  integration.status === "active" && (
                    <SquareCreditCard
                      sqaureUpRef={sqaureUpRef}
                      squareUpFormRef={squareUpFormRef}
                      clientId={integration.config.client_id}
                      locationId={integration.config.location?.id}
                      paymentAmountDetails={{
                        totalAmount: cartData?.total,
                        totalAmountFormatted: cartData?.formatted_total,
                        depositAmount: isDepositAmount,
                        depositAmountFormatted: cartData?.formatted_deposit,
                        totalTip: cartData?.tips.length ? cartData?.tips[0].amount : undefined,
                        totalTipFormatted: cartData?.tips.length
                          ? cartData?.tips[0].formatted_amount
                          : undefined,
                        totalAmountWithTip:
                          cartData?.full_amount_pay_now?.amount_in_units ??
                          cartData?.total_with_tip,
                        totalAmountWithTipFormatted: cartData?.formatted_total_with_tip,
                        depostAmountWithTip: cartData?.deposit_pay_now?.amount_in_units,
                        depostAmountWithTipFormatted: cartData?.formatted_deposit,
                        isDepositEnabledForItems:
                          Boolean(isDepositAmount) ||
                          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT,
                      }}
                      onSuccess={(paymentToken, values) => {
                        if (integration.id)
                          handleCreateOrder(
                            integration.id,
                            values.paymentType,
                            paymentToken,
                            parseFloat(`${values?.amount ?? "0"}`),
                            values.name || ""
                          )
                      }}
                      handleClose={() => {}}
                      hideDefaultContainer
                      isIntegratedPage={isIntegratedPage}
                      squarePaymentId={integration.id}
                      isAuthUser={Boolean(cartData?.customer?.is_member)}
                      showPendingBookingPaymentConfirmation={showPendingBookingPaymentConfirmation}
                      hideSavedCards
                      handlePaymentConfirmation={handlePaymentConfirmation}
                    />
                  )}
              </div>
            )
          })}
        </>
      ) : (
        <Tabs.Group
          aria-label={"Payment tab group"}
          style={"underline"}
          theme={{
            tabpanel: "shadow-none bg-transparent",
            tablist: { base: "tablist flex text-center sm:mx-6 mx-2" },
          }}
          onActiveTabChange={(tab) => setFieldValue("activeTab", tab)}
        >
          {filteredIntegrations?.map((integration, index) => {
            return (
              <Tabs.Item
                key={index}
                active
                tabIndex={index}
                title={
                  <div className={"flex flex-row items-center gap-1"}>
                    {integration.logo ? (
                      <CreditIcon />
                    ) : integration.name === "Gift Card" ? (
                      <GiftIcon />
                    ) : (
                      <CashIcon />
                    )}
                    {integratedData.length === 1 ? "Card" : integration.name}
                  </div>
                }
              >
                {integration.name.toLocaleLowerCase() === "stripe" &&
                  integration.status === "active" && (
                    <StripeCreditCard
                      paymentAmountDetails={{
                        totalAmount: cartData?.total,
                        totalAmountFormatted: cartData?.formatted_total,
                        depositAmount: isDepositAmount,
                        depositAmountFormatted: cartData?.formatted_deposit,
                        totalTip: cartData?.tips.length ? cartData?.tips[0].amount : undefined,
                        totalTipFormatted: cartData?.tips.length
                          ? cartData?.tips[0].formatted_amount
                          : undefined,
                        totalAmountWithTip:
                          cartData?.full_amount_pay_now?.amount_in_units ??
                          cartData?.total_with_tip,
                        totalAmountWithTipInCents:
                          cartData?.full_amount_pay_now?.amount ??
                          cartData?.total_with_tip_in_cents,
                        totalAmountWithTipFormatted: cartData?.formatted_total_with_tip,
                        depostAmountWithTip: cartData?.deposit_pay_now?.amount_in_units,
                        depostAmountWithTipFormatted: cartData?.formatted_deposit,
                        depositAmountWithTipInCents: cartData?.deposit_pay_now?.amount,
                        isDepositEnabledForItems:
                          Boolean(isDepositAmount) ||
                          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT,
                      }}
                      stripeKey={integration.config.client_id}
                      stripeAccountId={integration.config.stripe_account_id}
                      handleClose={() => {}}
                      stripeCreditCardFormRef={stripeCreditCardFormRef}
                      hideDefaultContainer
                      onError={handlePaymentError}
                      cartId={cartId}
                      stripePaymentId={integration.id}
                      isIntegratedPage={isIntegratedPage}
                      onOrderCreation={(orderId) => onsucess(orderId)}
                      giftcardDetails={giftcardDetails}
                      isWidgetsCard={!cartData?.customer?.is_member}
                      customer={cartData?.customer}
                      hideSavedCards
                      isAuthUser={Boolean(cartData?.customer?.is_member)}
                      showPendingBookingPaymentConfirmation={showPendingBookingPaymentConfirmation}
                      handlePaymentConfirmation={handlePaymentConfirmation}
                      isWalletsEnabled={
                        posthog.isFeatureEnabled("enable_wallets") && Number(values?.amount) > 0
                      }
                    />
                  )}

                {integration.name.toLocaleLowerCase() === "squareup" &&
                  integration.status === "active" && (
                    <SquareCreditCard
                      sqaureUpRef={sqaureUpRef}
                      squareUpFormRef={squareUpFormRef}
                      clientId={integration.config.client_id}
                      locationId={integration.config.location?.id}
                      paymentAmountDetails={{
                        totalAmount: cartData?.total,
                        totalAmountFormatted: cartData?.formatted_total,
                        depositAmount: isDepositAmount,
                        depositAmountFormatted: cartData?.formatted_deposit,
                        totalTip: cartData?.tips.length ? cartData?.tips[0].amount : undefined,
                        totalTipFormatted: cartData?.tips.length
                          ? cartData?.tips[0].formatted_amount
                          : undefined,
                        totalAmountWithTip:
                          cartData?.full_amount_pay_now?.amount_in_units ??
                          cartData?.total_with_tip,
                        totalAmountWithTipFormatted: cartData?.formatted_total_with_tip,
                        depostAmountWithTip: cartData?.deposit_pay_now?.amount_in_units,
                        depostAmountWithTipFormatted: cartData?.formatted_deposit,
                        isDepositEnabledForItems:
                          Boolean(isDepositAmount) ||
                          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT,
                      }}
                      onSuccess={(paymentToken, values) => {
                        if (integration.id)
                          handleCreateOrder(
                            integration.id,
                            values.paymentType,
                            paymentToken,
                            parseFloat(`${values?.amount ?? "0"}`),
                            values.name || ""
                          )
                      }}
                      isWidgetsCard={!cartData?.customer?.is_member}
                      handleClose={() => {}}
                      hideDefaultContainer
                      isIntegratedPage={isIntegratedPage}
                      squarePaymentId={integration.id}
                      isAuthUser={Boolean(cartData?.customer?.is_member)}
                      hideSavedCards
                      showPendingBookingPaymentConfirmation={showPendingBookingPaymentConfirmation}
                      handlePaymentConfirmation={handlePaymentConfirmation}
                    />
                  )}
              </Tabs.Item>
            )
          })}
        </Tabs.Group>
      )}
    </>
  )
}

export default PaymentSection
